import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import { X, Check } from 'lucide-react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { ChatFilled } from 'src/shared/components/icons/chat/chat-filled'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Card, CardTitle, CardDescription } from 'src/shared/components/ui/cards/card'
import { Avatar, AvatarImage, AvatarFallback } from 'src/shared/components/ui/miscellaneous/avatar'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { Separator } from 'src/shared/components/ui/miscellaneous/separator'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { cn } from 'src/shared/utils/cn'

const cardVariants = cva('flex items-center justify-between p-4 transition-colors', {
  variants: {
    variant: {
      default: 'bg-background',
      secondary: 'bg-secondary/5',
      emphasized: 'bg-accent',
      noBorder: 'rounded-none border-none shadow-none',
    },
    interactive: {
      true: 'cursor-pointer hover:bg-secondary/10',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    interactive: false,
  },
})

interface ReferralListCardProps extends VariantProps<typeof cardVariants> {
  avatarUrl?: string
  title: string
  subtitle?: string
  SubtitleIcon?: LucideIcon
  tag?: string
  tagVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'warning' | 'accent'
  TagIcon?: LucideIcon
  secondaryTag?: string
  secondaryTagVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'warning' | 'accent'
  secondaryTagIcon?: LucideIcon
  onClick?: () => void
  isSelected?: boolean | null
  showMessageButton?: boolean
  showChat?: boolean
  chatId?: string
  unreadCount?: number
  className?: string
  showActionSymbols?: boolean
  topLeftIcon?: LucideIcon
  topLeftIconColor?: string
}

export const ReferralListCard: React.FC<ReferralListCardProps> = ({
  avatarUrl,
  title,
  subtitle,
  SubtitleIcon,
  tag,
  tagVariant = 'secondary',
  TagIcon,
  secondaryTag,
  secondaryTagVariant = 'secondary',
  secondaryTagIcon,
  isSelected,
  onClick,
  showMessageButton,
  showChat,
  chatId,
  unreadCount = 0,
  variant = 'default',
  className,
  showActionSymbols = false,
  topLeftIcon: TopLeftIcon,
  topLeftIconColor = 'bg-primary',
}) => {
  const navigate = useNavigate()

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (chatId) {
      navigate(paths.chats.conversation.referral.getHref(chatId))
    }
  }

  return (
    <>
      <Card
        className={cn(
          cardVariants({
            variant: isSelected ? 'secondary' : variant,
            interactive: !!onClick,
          }),
          className,
        )}
        onClick={onClick}
      >
        <div className="flex items-center">
          {avatarUrl && (
            <div className="relative mr-4">
              <Avatar className="size-[45px]">
                <AvatarImage src={avatarUrl} alt={title} />
                <AvatarFallback>{title.charAt(0)}</AvatarFallback>
              </Avatar>
              {showActionSymbols && (
                <div className="absolute -bottom-2 flex w-full justify-center gap-1">
                  <div className="rounded-full bg-destructive/90 p-0.5">
                    <X className="size-3 text-background" />
                  </div>
                  <div className="rounded-full bg-primary/90 p-0.5">
                    <Check className="size-3 text-background" />
                  </div>
                </div>
              )}
              {TopLeftIcon && (
                <div
                  className={cn(
                    'absolute -left-1 -top-1 size-5 rounded-full',
                    topLeftIconColor,
                    'flex items-center justify-center',
                  )}
                >
                  <TopLeftIcon className="size-4 text-background" />
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center">
              <CardTitle className="text-sm font-semibold">{title}</CardTitle>
            </div>
            <CardDescription className="flex items-center text-xs text-muted">
              {SubtitleIcon && <SubtitleIcon className="mr-1 size-3 text-gray-500" />}
              {subtitle}
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-row items-center gap-3">
          {tag && <Badge variant={tagVariant} text={tag} icon={TagIcon} className="ml-2" size="small" />}
          {secondaryTag && (
            <Badge
              variant={secondaryTagVariant}
              text={secondaryTag}
              icon={secondaryTagIcon}
              className="ml-2"
              size="small"
            />
          )}
          {showChat && (
            <Button onClick={handleChatClick} variant="default" size="icon" className="shrink-0 rounded-full">
              {unreadCount > 0 && (
                <span className="size-5 rounded-full bg-destructive text-xs font-medium text-destructive-foreground">
                  {unreadCount}
                </span>
              )}
              <ChatFilled className="size-5" />
            </Button>
          )}
          {showMessageButton && <MessageButton />}
        </div>
      </Card>
      {variant === 'noBorder' && <Separator />}
    </>
  )
}

const MessageButton = () => (
  <button
    onClick={() => {
      alert('TODO: Message logic')
    }}
    className="relative flex size-10 items-center justify-center rounded-full border border-muted/50 bg-background text-foreground"
  >
    <span className="absolute right-0 top-0 flex h-5 w-4 items-center justify-center rounded-full bg-destructive text-xs text-destructive-foreground">
      2
    </span>
    <ChatFilled className="size-5" />
  </button>
)

export const SkeletonReferralListCard: React.FC<{ hidden?: boolean }> = ({ hidden = false }) => {
  return (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center">
        <Skeleton className={`mr-4 size-8 rounded-lg ${hidden && 'bg-transparent'}`} />
        <div>
          <Skeleton className={`mb-1 h-3 w-24 ${hidden && 'bg-transparent'}`} />
          <Skeleton className={`h-2 w-36 ${hidden && 'bg-transparent'}`} />
        </div>
      </div>
      <div className="flex items-center">
        <Skeleton className={`mr-1 h-5 w-16 ${hidden && 'bg-transparent'}`} />
        <Skeleton className={`size-4 rounded-full ${hidden && 'bg-transparent'}`} />
      </div>
    </div>
  )
}

export const SkeletonReferralListCardList: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonReferralListCard key={index} />
      ))}
    </div>
  )
}
